import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'
import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'
import moment from 'moment'

import {
  Drawer,
  ToggleButton,
  useColumnVisibility
} from '~/components/ColumnSelector'
import DatepickerHeader from '~/components/datepicker-header'
import EmptyPage from '~/components/empty-page'
import Panel from '~/components/Panel'
import Table from '~/components/Table'
import { formatDateToString, history } from '~/helpers'
import { useGetFetchQuery } from '~/hooks/useGetFetchQuery'
import useQueryParams from '~/hooks/useQuery'
import LookbackSelector from '~/modules/retailMedia/components/LookbackSelector'
import { parseLookbackMonths } from '~/modules/retailMedia/constants'
import {
  getNtbOrdersWindow,
  hasVisibleNtbColumn
} from '~/modules/retailMedia/helpers/newMetricsAvailability'
import { useNtbAvailability } from '~/modules/retailMedia/hooks/useNtbAvailability'
import { useAppSelector } from '~/store/hooks'

import { formatIndustryReportHistoryCampaign } from '../../dtos/industry/campaign/reportHistory'
import {
  getCampaignHistoryReportService,
  getCampaignsReportHistoryKey
} from '../../services/report'
import { getSchema } from './schema'
import { useDigitalSignageSchema } from './useDigitalSignageSchema'

type DailyHistorySchema = ReturnType<typeof getSchema>

const DAILY_HISTORY_READONLY_KEYS = ['data'] as const satisfies ReadonlyArray<
  keyof DailyHistorySchema
>

const DIGITAL_SIGNAGE_READONLY_KEYS = [
  'place_name'
] as const satisfies ReadonlyArray<'place_name'>

interface Props {
  campaignType: CampaignAdType
  campaignStartDate?: string
  target?: CampaignTarget
}

const DailyHistoryTable = ({
  campaignType,
  campaignStartDate,
  target
}: Props) => {
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false)
  const { id } = useParams()

  const searchParams = useQueryParams()
  const lookbackMonths = parseLookbackMonths(
    searchParams.get('lookback_months')
  )

  const handleLookbackChange = useCallback(
    (value: number) => {
      searchParams.set('lookback_months', String(value))
      history.push({ search: searchParams.toString() })
    },
    [searchParams]
  )

  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)

  const queryParams = useMemo(
    () => ({
      start_date: formatDateToString(startDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(endDate, 'YYYY-MM-DD'),
      campaign_id: id,
      ad_type: campaignType,
      target,
      lookback_months: lookbackMonths
    }),
    [startDate, endDate, id, campaignType, target, lookbackMonths]
  )

  const queryKey = useMemo(
    () => [getCampaignsReportHistoryKey, id, ...Object.values(queryParams)],
    [id, queryParams]
  )

  const cachedData = useGetFetchQuery(queryKey)

  const {
    data: response,
    isLoading: loading,
    error: error
  } = useQuery({
    enabled: !cachedData,
    queryKey: queryKey,
    queryFn: async () => {
      const { data } = await getCampaignHistoryReportService(queryParams)

      // remove dates before campaign start date
      const filterDates = data.filter(item => {
        if (!campaignStartDate) return true

        const groupedDate = moment(item.groupby)

        const startDate = moment(campaignStartDate)

        const isSameOrAfter = groupedDate.isSameOrAfter(startDate)

        return isSameOrAfter
      })

      return formatIndustryReportHistoryCampaign(filterDates, campaignType)
    }
  })

  const data = cachedData || response
  const digitalSignageSchema = useDigitalSignageSchema(data?.raw)
  const metricsSources = useMemo(() => data?.raw || [], [data?.raw])
  const availableMetrics = useNtbAvailability(metricsSources)
  const standardSchema = useMemo(
    () =>
      getSchema({
        campaignType,
        availableMetrics,
        lookbackMonths,
        ntbOrdersWindow: getNtbOrdersWindow(endDate)
      }),
    [availableMetrics, campaignType, lookbackMonths, endDate]
  )
  const isDigitalSignage = useMemo(() => {
    return campaignType === 'digital_signage'
  }, [campaignType])
  const handleOpenColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(true)
  }, [])

  const handleCloseColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(false)
  }, [])

  const tableSchema = useMemo(
    () => (isDigitalSignage ? digitalSignageSchema.schema : standardSchema),
    [isDigitalSignage, digitalSignageSchema.schema, standardSchema]
  )
  const tableReadonlyKeys = useMemo(
    () =>
      isDigitalSignage
        ? DIGITAL_SIGNAGE_READONLY_KEYS
        : DAILY_HISTORY_READONLY_KEYS,
    [isDigitalSignage]
  )
  const tableName: ColumnSelectorTables = useMemo(
    () => (isDigitalSignage ? 'daily_history_digital' : 'daily_history'),
    [isDigitalSignage]
  )

  const columnVisibility = useColumnVisibility({
    schema: tableSchema,
    tableName,
    readonlyKeys: tableReadonlyKeys
  })

  const isEmpty = useMemo(
    () =>
      data?.raw?.length === 0 ||
      (isDigitalSignage && digitalSignageSchema?.data?.length === 0),
    [data?.raw?.length, isDigitalSignage, digitalSignageSchema?.data?.length]
  )

  const tableData = useMemo(
    () => ({
      schema: columnVisibility.visibleColumnsSchema,
      data: isDigitalSignage ? digitalSignageSchema.data : data?.raw || []
    }),
    [
      data?.raw,
      digitalSignageSchema.data,
      isDigitalSignage,
      columnVisibility.visibleColumnsSchema
    ]
  )

  const activeColumnVisibility = columnVisibility
  const shouldShowColumnSelector = !!tableData.data.length && !isEmpty
  // seletor de lookback só aparece quando há coluna NTB visível/selecionada
  const hasNtbColumnVisible = hasVisibleNtbColumn(tableData.schema)

  return (
    <>
      {shouldShowColumnSelector && (
        <div
          className="d-flex justify-content-end mb-2"
          style={{ gap: '.5rem' }}
        >
          {hasNtbColumnVisible && (
            <LookbackSelector
              value={lookbackMonths}
              onChange={handleLookbackChange}
            />
          )}
          <ToggleButton onClick={handleOpenColumnSelector} />
        </div>
      )}
      <Drawer
        columns={activeColumnVisibility.columns}
        isOpen={isColumnSelectorOpen}
        onClose={handleCloseColumnSelector}
        onColumnsChange={activeColumnVisibility.onColumnsChange}
        onResetToDefault={activeColumnVisibility.onResetToDefault}
      />

      <Panel noPaddingBody={!!data?.raw?.length} isLoading={loading}>
        {isEmpty && (
          <EmptyPage
            title={t('rm:daily.history.table.emptyPage.title')}
            subtitle={t('rm:daily.history.table.emptyPage.subtitle')}
            svgPath="photos/orders-empty"
          >
            <DatepickerHeader />
          </EmptyPage>
        )}

        {error && (
          <Alert variant="critical">
            {t('rm:daily.history.table.alert.error')}
          </Alert>
        )}
        {data?.raw && !!data?.raw?.length && !isEmpty && (
          <Table schema={tableData.schema} data={tableData.data} sideBorder />
        )}
      </Panel>
    </>
  )
}

export default DailyHistoryTable
